import type { ComponentType } from 'react';

import { cn } from '@/lib/utils';

interface ViewToggleOption<T extends string> {
  value: T;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  testId?: string;
}

interface Props<T extends string> {
  value: T;
  options: ViewToggleOption<T>[];
  onChange: (value: T) => void;
  'aria-label'?: string;
}

/** A small segmented control — one button per view, the active one filled. */
export function ViewToggle<T extends string>({
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
}: Props<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-lg border bg-background p-0.5"
    >
      {options.map((option) => {
        const active = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            data-testid={option.testId}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
